import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, Modal, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { ClientConfigForm } from '@/components/config/ClientConfigForm';
import { ClientNotesField } from '@/components/config/ClientNotesField';
import { describeConfigFileError, readConfigFile } from '@/components/config/configFile';
import { serializeForDiff } from '@/components/config/jsonDiff';
import { QuotaCheckCard } from '@/components/tools/QuotaCheckCard';
import { ToolPage } from '@/components/ui/ToolPage';
import { DEFAULT_REGION } from '@/config/clientConfigFields';
import {
  EMPTY_CONTRACT,
  canonicalPartner,
  defaultContractForOrg,
  normalisePartner,
  partnerNames,
  type ContractFormState,
} from '@/config/contract';
import { useClientFlags } from '@/hooks/useClientFlags';
import { classifyAccount, getNextgenAccountIds } from '@/services/accountOrgService';
import { clientService } from '@/services/clientService';
import { clientMetadataService } from '@/services/clientMetadataService';
import { ACCOUNT_ORG_VALUES, type AccountOrgValue, type ClientConfig } from '@/types';
import { validateClientName, sanitizeClientName } from '@/utils/clientValidation';

/** What the last create did, kept so the page can say it back. */
interface Created {
  name: string;
  /** The config was written but the contract and notes were not. */
  metadataFailed: boolean;
}

/** The four views of a new client. Their buttons sit in the client card. */
const CREATE_TABS = [
  { key: 'config', label: 'Config' },
  { key: 'metadata', label: 'Notes' },
  { key: 'quotas', label: 'Bedrock limits' },
  { key: 'json', label: 'JSON file' },
] as const;
type CreateTab = (typeof CREATE_TABS)[number]['key'];

export default function CreateClientConfig() {
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<CreateTab>('config');
  // The slot in the left column where the config form puts its rail.
  const [railHost, setRailHost] = useState<HTMLDivElement | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientNameError, setClientNameError] = useState<string | null>(null);
  const [clientAccountId, setClientAccountId] = useState('');
  // Mirrors the form's Region field so the Bedrock limits tab uses the live value
  const [region, setRegion] = useState(DEFAULT_REGION);
  // Names that already have a config. Creating one of them again replaces it,
  // so the page warns before the person does.
  const [existingNames, setExistingNames] = useState<Set<string>>(new Set());

  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Created | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [pendingConfig, setPendingConfig] = useState<ClientConfig | null>(null);

  // The client's record in the portal's own table (not deployed)
  const [contract, setContract] = useState<ContractFormState>(EMPTY_CONTRACT);
  // True once the person picks a contract. A suggestion never replaces their choice.
  const [contractChosen, setContractChosen] = useState(false);
  const [contractSuggestion, setContractSuggestion] = useState<string | undefined>(undefined);
  const [metaNotes, setMetaNotes] = useState('');
  const flags = useClientFlags();
  const knownPartners = partnerNames(flags.metadata.values());
  // The organisation the setup tool named (`?org=`), when the person came from one.
  const [orgFromSetup, setOrgFromSetup] = useState<AccountOrgValue | null>(null);

  // Developer JSON create
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [uploadedConfig, setUploadedConfig] = useState<ClientConfig | null>(null);
  const [showJsonPreview, setShowJsonPreview] = useState(false);

  // Prefill from query params when available
  useEffect(() => {
    const qClient = (searchParams.get('clientName') || '').trim();
    const qAccount = (searchParams.get('accountId') || '').trim();
    const qOrg = (searchParams.get('org') || '').trim();
    if (qClient && !clientName) setClientName(qClient);
    if (qAccount && !clientAccountId) setClientAccountId(qAccount);
    if ((ACCOUNT_ORG_VALUES as readonly string[]).includes(qOrg)) setOrgFromSetup(qOrg as AccountOrgValue);
    // only run once on mount
  }, []);

  // Suggest the contract from the account's organisation: Credits in NextGen, Own account in a
  // customer's own account, Internal in Arcanum's. The lookup only reads. When it fails, the
  // contract stays empty and the person chooses.
  useEffect(() => {
    if (contractChosen) return;
    const account = clientAccountId.trim();
    let cancelled = false;
    const suggest = (org: AccountOrgValue | null) => {
      if (cancelled) return;
      const suggested = defaultContractForOrg(org);
      setContract((prev) => ({ ...prev, contract: suggested }));
      setContractSuggestion(
        org === 'nextgen'
          ? 'the account is in NextGen'
          : org === 'standalone'
            ? "the account is the customer's own"
            : org === 'arcanum'
              ? "the account is one of Arcanum's own"
              : undefined
      );
    };
    if (orgFromSetup) {
      suggest(orgFromSetup);
    } else if (/^\d{12}$/.test(account)) {
      void getNextgenAccountIds().then((ids) => suggest(classifyAccount(account, ids)));
    } else {
      suggest(null);
    }
    return () => {
      cancelled = true;
    };
  }, [clientAccountId, orgFromSetup, contractChosen]);

  const changeContract = (next: ContractFormState) => {
    if (next.contract !== contract.contract) setContractChosen(true);
    setContract(next);
  };

  useEffect(() => {
    (async () => {
      try {
        const list = await clientService.getAllClients();
        setExistingNames(new Set(list.map((c) => c.name)));
      } catch {
        // Without the list the page cannot warn about a taken name. Creating still works.
      }
    })();
  }, []);

  // Validate client name whenever it changes
  useEffect(() => {
    if (clientName.trim().length > 0) {
      const error = validateClientName(clientName);
      setClientNameError(error);
    } else {
      setClientNameError(null);
    }
  }, [clientName]);

  // The form is long and its save button stays in view, so a message at the
  // top of the page can land off screen. Bring it to the person.
  const messagesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error || created) messagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error, created]);

  const handleFormSubmit = (minimal: Partial<ClientConfig>) => {
    setError(null);
    setCreated(null);
    if (!clientName || !clientAccountId) {
      setError('Enter a client name and an AWS account ID first.');
      return;
    }
    if (clientNameError) {
      setError(`Fix the client name first: ${clientNameError}`);
      return;
    }
    setPendingConfig({ ...minimal, clientAccountId } as ClientConfig);
    setShowPreview(true);
  };

  const createFromForm = async () => {
    if (!pendingConfig) return;
    setError(null);
    try {
      setSubmitting(true);
      await clientService.createClientConfig(clientName, pendingConfig);
      let metadataFailed = false;
      if (contract.contract) {
        const trial = contract.contract === 'trial';
        const partner =
          contract.contract === 'partner' ? canonicalPartner(normalisePartner(contract.partner), knownPartners) : '';
        try {
          // Only the fields set here are written. On a name that already has a record (a
          // config made again), everything else on it stays: its organisation and its tags.
          await clientMetadataService.updateFields(clientName, {
            contract: contract.contract,
            partner: partner || null,
            trialStartDate: trial && contract.trialStartDate ? contract.trialStartDate : null,
            trialEndDate: trial && contract.trialEndDate ? contract.trialEndDate : null,
            ...(metaNotes ? { notes: metaNotes } : {}),
          });
        } catch (metaErr) {
          console.error('Failed to save the contract:', metaErr);
          metadataFailed = true;
        }
      }
      setCreated({ name: clientName, metadataFailed });
      setExistingNames((prev) => new Set(prev).add(clientName));
      setShowPreview(false);
    } catch (err) {
      const detail = err instanceof Error ? ` ${err.message}` : '';
      setError(`The config was not created.${detail}`);
      setShowPreview(false);
    } finally {
      setSubmitting(false);
    }
  };

  const createFromFile = async () => {
    if (!uploadedConfig || !clientName || clientNameError) return;
    setError(null);
    try {
      setSubmitting(true);
      await clientService.createClientConfig(clientName, uploadedConfig);
      setCreated({ name: clientName, metadataFailed: false });
      setExistingNames((prev) => new Set(prev).add(clientName));
      setShowJsonPreview(false);
      setUploadedConfig(null);
    } catch (err) {
      const detail = err instanceof Error ? ` ${err.message}` : '';
      setError(`The config was not created.${detail}`);
      setShowJsonPreview(false);
    } finally {
      setSubmitting(false);
    }
  };

  const name = clientName.trim();
  const nameTaken = !!name && existingNames.has(name) && created?.name !== name;
  const suggestion = sanitizeClientName(clientName);

  const replaceWarning = nameTaken && (
    <Alert variant="warning" className="mb-3">
      <strong>{name}</strong> already has a config. Creating this one replaces every setting in it.
    </Alert>
  );

  return (
    <ToolPage
      title="Create client config"
      subtitle="Write the config for a new client. Nothing is deployed until you run the client's first deploy."
    >
      <Row>
        {/* The left column: the new client, its four views, and the config form's rail. */}
        <Col md={4} lg={3} xl={2} className="mb-3">
          <div className="client-config-side">
            <Card>
              <Card.Body className="client-config-side__client">
                <h2 className="section-label mb-0">Client</h2>
                <Form.Group controlId="create-client-name">
                  <Form.Label>Client name</Form.Label>
                  <Form.Control
                    size="sm"
                    className="mono"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="arcanum-demo"
                    isInvalid={!!clientNameError}
                  />
                  {clientNameError ? (
                    <>
                      <Form.Control.Feedback type="invalid">{clientNameError}</Form.Control.Feedback>
                      {suggestion && suggestion !== clientName && (
                        <Form.Text>
                          Try <span className="mono">{suggestion}</span>.
                        </Form.Text>
                      )}
                    </>
                  ) : nameTaken ? (
                    <Form.Text className="text-warning">
                      This name already has a config, and creating it again replaces that config. To change the client,
                      use{' '}
                      <Link to={`/tools/update-client-config?clientName=${encodeURIComponent(name)}`}>
                        Update client config
                      </Link>
                      .
                    </Form.Text>
                  ) : (
                    <Form.Text>Lowercase letters, numbers and dashes. It makes the web address.</Form.Text>
                  )}
                </Form.Group>
                <Form.Group controlId="create-account-id">
                  <Form.Label>AWS account ID</Form.Label>
                  <Form.Control
                    size="sm"
                    className="mono"
                    value={clientAccountId}
                    onChange={(e) => setClientAccountId(e.target.value)}
                    placeholder="123456789012"
                    inputMode="numeric"
                  />
                  <Form.Text>The 12-digit account the client is deployed into.</Form.Text>
                </Form.Group>
                <Nav
                  variant="pills"
                  className="nav-pills-custom client-config-side__views"
                  activeKey={activeTab}
                  onSelect={(key) => key && setActiveTab(key as CreateTab)}
                  aria-label="What to set"
                >
                  {CREATE_TABS.map((t) => (
                    <Nav.Item key={t.key}>
                      <Nav.Link eventKey={t.key}>{t.label}</Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
                <p className="tool-form__hint mb-0">Data sources and a custom domain: ask a developer.</p>
              </Card.Body>
            </Card>
            <div ref={setRailHost} />
          </div>
        </Col>
        <Col md={8} lg={9} xl={10}>
          <div ref={messagesRef}>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {created && (
              <Alert variant="success" dismissible onClose={() => setCreated(null)}>
                Created the config for <strong>{created.name}</strong>. It is not deployed yet:{' '}
                <Link to="/deployments">start its first deploy</Link>.
                {created.metadataFailed && (
                  <div className="mt-1">
                    The contract and notes were not saved. Set them from{' '}
                    <Link to={`/tools/update-client-config?clientName=${encodeURIComponent(created.name)}`}>
                      Update client config
                    </Link>
                    .
                  </div>
                )}
              </Alert>
            )}
          </div>

          <Tab.Container activeKey={activeTab}>
            <Tab.Content>
              {/* Tab 1: the config form */}
              <Tab.Pane eventKey="config">
                <ClientConfigForm
                  mode="create"
                  working={submitting}
                  submitLabel="Review and create"
                  submitDisabled={!!clientNameError}
                  submitHint={
                    clientNameError
                      ? 'Fix the client name to continue.'
                      : 'A setting you leave alone keeps its default.'
                  }
                  onSubmit={handleFormSubmit}
                  onError={setError}
                  onRegionChange={setRegion}
                  contract={contract}
                  onContractChange={changeContract}
                  partnerNames={knownPartners}
                  railHost={activeTab === 'config' ? railHost : null}
                  contractSuggestion={contractChosen ? undefined : contractSuggestion}
                />
              </Tab.Pane>

              {/* Tab 2: notes */}
              <Tab.Pane eventKey="metadata">
                <Card>
                  <Card.Body>
                    <h2 className="section-label">Notes</h2>
                    <p className="tool-form__hint">
                      Optional. What the team should know about the client. Notes are not deployed. They are saved with
                      the config when you create it on the Config tab.
                    </p>
                    <ClientNotesField notes={metaNotes} onNotesChange={setMetaNotes} />
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Tab 3: Bedrock limits for the account */}
              <Tab.Pane eventKey="quotas">
                {clientAccountId && region ? (
                  <QuotaCheckCard accountId={clientAccountId} region={region} disabled={submitting} />
                ) : (
                  <Card>
                    <Card.Body className="tool-form__hint">
                      Enter an AWS account ID above to check the account's Bedrock limits.
                    </Card.Body>
                  </Card>
                )}
              </Tab.Pane>

              {/* Tab 4: create from a whole JSON file */}
              <Tab.Pane eventKey="json">
                <Card>
                  <Card.Body>
                    <div className="tool-form">
                      <section className="tool-form__section">
                        <h2 className="section-label">Create from a JSON file</h2>
                        <p className="tool-form__hint">
                          For developers. Upload a whole config as JSON. It is written under the client name above, and
                          the account ID in the file is used, not the one above. The contract and notes are not saved
                          this way: set them in Update client config.
                        </p>
                        <Form.Group controlId="create-json-file">
                          <Form.Label>Config file</Form.Label>
                          <Form.Control
                            type="file"
                            accept="application/json,.json"
                            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                              setJsonError(null);
                              setUploadedConfig(null);
                              const file = e.currentTarget.files?.[0];
                              if (!file) return;
                              try {
                                setUploadedConfig(await readConfigFile(file));
                              } catch (err: unknown) {
                                setJsonError(describeConfigFileError(err));
                              }
                            }}
                          />
                          <Form.Text>A complete config. A setting the portal does not know is refused.</Form.Text>
                        </Form.Group>
                        {jsonError && (
                          <Alert variant="danger" className="mt-3 mb-0">
                            {jsonError}
                          </Alert>
                        )}
                        {uploadedConfig && (
                          <p className="tool-form__hint mt-3">
                            The file is valid and holds {Object.keys(uploadedConfig).length} settings.
                          </p>
                        )}
                      </section>
                      <div className="tool-form__actions">
                        <Button
                          variant="primary"
                          disabled={!uploadedConfig || !clientName || !!clientNameError}
                          onClick={() => setShowJsonPreview(true)}
                        >
                          Review and create
                        </Button>
                        {uploadedConfig && (!clientName || !!clientNameError) && (
                          <span className="tool-form__hint">Enter a valid client name above first.</span>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Col>
      </Row>

      <Modal show={showPreview} onHide={() => !submitting && setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Create this config?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This writes a config for <strong>{clientName || 'the client'}</strong> in AWS account{' '}
            <span className="mono">{clientAccountId}</span>. Nothing is deployed until you start the client's first
            deploy.
          </p>
          {replaceWarning}
          <pre className="tool-json">
            {pendingConfig ? serializeForDiff(pendingConfig as Record<string, unknown>) : ''}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowPreview(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" disabled={submitting} onClick={createFromForm}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Creating
              </>
            ) : (
              'Create config'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showJsonPreview} onHide={() => !submitting && setShowJsonPreview(false)} size="lg" centered>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Create this config from the file?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This writes the file below as the config for <strong>{clientName || 'the client'}</strong>. Nothing is
            deployed until you start the client's first deploy.
          </p>
          {replaceWarning}
          <pre className="tool-json">{uploadedConfig ? JSON.stringify(uploadedConfig, null, 2) : ''}</pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowJsonPreview(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!uploadedConfig || submitting} onClick={createFromFile}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Creating
              </>
            ) : (
              'Create config'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </ToolPage>
  );
}
